import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PendingToggle } from '../PartnerConfig.types'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, useQuery } from '@percent/admin-dashboard/common/hooks'
import type {
  DonationGiftNoteConfigValue,
  DonationMatchingDirectToCampaignConfigValue,
  DonationMatchingFamilialConfigValue,
  DonationTransactionalEmailsConfigValue,
  FundraisingPublicFundraisersConfigValue,
  GivingFeaturesConfigValue,
  GivingTransactionalEmailsConfigValue
} from '@percent/admin-dashboard/api/types'

const HTTP_NOT_FOUND = 404

export function usePartnerConfig(partnerId: string) {
  const { adminService } = useServices()
  const { t } = useTranslation()
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null)
  const refreshOnSuccessRef = useRef<(() => void) | null>(null)

  const [
    {
      data: givingFeaturesData,
      isLoading: givingFeaturesLoading,
      errorMessage: givingFeaturesError,
      status: givingFeaturesStatus
    },
    { refresh: refreshGivingFeatures }
  ] = useQuery(adminService.queries.getPartnerGivingFeaturesConfig, { partnerId })

  const [
    {
      data: givingEmailsData,
      isLoading: givingEmailsLoading,
      errorMessage: givingEmailsError,
      status: givingEmailsStatus
    },
    { refresh: refreshGivingEmails }
  ] = useQuery(adminService.queries.getPartnerGivingEmailsConfig, { partnerId })

  const [
    {
      data: donationGiftNoteData,
      isLoading: donationGiftNoteLoading,
      errorMessage: donationGiftNoteError,
      status: donationGiftNoteStatus
    },
    { refresh: refreshDonationGiftNote }
  ] = useQuery(adminService.queries.getPartnerDonationGiftNoteConfig, { partnerId })

  const [
    {
      data: donationEmailsData,
      isLoading: donationEmailsLoading,
      errorMessage: donationEmailsError,
      status: donationEmailsStatus
    },
    { refresh: refreshDonationEmails }
  ] = useQuery(adminService.queries.getPartnerDonationEmailsConfig, { partnerId })

  const [
    { data: fundraisingData, isLoading: fundraisingLoading, errorMessage: fundraisingError, status: fundraisingStatus },
    { refresh: refreshFundraising }
  ] = useQuery(adminService.queries.getPartnerFundraisingPublicFundraisersConfig, { partnerId })

  const [
    {
      data: donationMatchingData,
      isLoading: donationMatchingLoading,
      errorMessage: donationMatchingError,
      status: donationMatchingStatus
    },
    { refresh: refreshDonationMatching }
  ] = useQuery(adminService.queries.getPartnerDonationMatchingFamilialConfig, { partnerId })

  const [
    {
      data: directToCampaignData,
      isLoading: directToCampaignLoading,
      errorMessage: directToCampaignError,
      status: directToCampaignStatus
    },
    { refresh: refreshDirectToCampaign }
  ] = useQuery(adminService.queries.getPartnerDonationMatchingDirectToCampaignConfig, { partnerId })

  const handleMutationSuccess = () => {
    refreshOnSuccessRef.current?.()
    refreshOnSuccessRef.current = null
    setPendingToggle(null)
  }

  const [
    { isLoading: givingMutating, errorMessage: givingMutationError },
    { apiFunc: updateGiving, setErrorMessage: resetGivingError }
  ] = useMutation(adminService.commands.updatePartnerGivingConfig, handleMutationSuccess)

  const [
    { isLoading: donationMutating, errorMessage: donationMutationError },
    { apiFunc: updateDonation, setErrorMessage: resetDonationError }
  ] = useMutation(adminService.commands.updatePartnerDonationConfig, handleMutationSuccess)

  const [
    { isLoading: fundraisingMutating, errorMessage: fundraisingMutationError },
    { apiFunc: updateFundraising, setErrorMessage: resetFundraisingError }
  ] = useMutation(adminService.commands.updatePartnerFundraisingConfig, handleMutationSuccess)

  const [
    { isLoading: donationMatchingMutating, errorMessage: donationMatchingMutationError },
    { apiFunc: updateDonationMatching, setErrorMessage: resetDonationMatchingError }
  ] = useMutation(adminService.commands.updatePartnerDonationMatchingConfig, handleMutationSuccess)

  const givingFeatures = givingFeaturesData?.value
  const givingEmails = givingEmailsData?.value
  const donationGiftNote = donationGiftNoteData?.value
  const donationEmails = donationEmailsData?.value
  const fundraisingConfig = fundraisingData?.value
  const donationMatchingConfig = donationMatchingData?.value
  const directToCampaignConfig = directToCampaignData?.value

  const handleGivingFeaturesToggle = (
    field: 'profileSecondaryEmails' | 'opportunityTimeLogsRewardsEligibility',
    newEnabled: boolean
  ) => {
    // Falls back to 'disabled' when no config exists yet (404 → undefined), consistent
    // with the 404-as-disabled convention. The PUT will create the full record on first write.
    const newValue: GivingFeaturesConfigValue = {
      ...(givingFeatures ?? {}),
      [field]: newEnabled ? 'enabled' : 'disabled'
    }

    resetGivingError('')
    setPendingToggle({
      label: t(`partnerConfig.giving.features.${field}`),
      newEnabled,
      domain: 'giving',
      // refreshOnSuccessRef is set here (not at mount) so it always captures the correct
      // refresh for this specific toggle. The modal blocks further interaction while a
      // mutation is in-flight, so only one mutation can be active at a time.
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshGivingFeatures
        updateGiving({ partnerId, key: 'features', value: newValue })
      }
    })
  }

  const handleGivingEmailsToggle = (newEnabled: boolean) => {
    const newValue: GivingTransactionalEmailsConfigValue = {
      ...(givingEmails ?? {}),
      welcomeEmail: newEnabled ? 'enabled' : 'disabled'
    }

    resetGivingError('')
    setPendingToggle({
      label: t('partnerConfig.giving.transactionalEmails.welcomeEmail'),
      newEnabled,
      domain: 'giving',
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshGivingEmails
        updateGiving({ partnerId, key: 'transactional_emails', value: newValue })
      }
    })
  }

  const handleDonationGiftNoteToggle = (newEnabled: boolean) => {
    const newValue: DonationGiftNoteConfigValue = { ...(donationGiftNote ?? {}), enabled: newEnabled }

    resetDonationError('')
    setPendingToggle({
      label: t('partnerConfig.donation.giftNote'),
      newEnabled,
      domain: 'donation',
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshDonationGiftNote
        updateDonation({ partnerId, key: 'gift_note', value: newValue })
      }
    })
  }

  const handleDonationEmailsToggle = (newEnabled: boolean) => {
    const newValue: DonationTransactionalEmailsConfigValue = {
      ...(donationEmails ?? {}),
      walletFundsEmail: newEnabled ? 'enabled' : 'disabled'
    }

    resetDonationError('')
    setPendingToggle({
      label: t('partnerConfig.donation.transactionalEmails.walletFundsEmail'),
      newEnabled,
      domain: 'donation',
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshDonationEmails
        updateDonation({ partnerId, key: 'transactional_emails', value: newValue })
      }
    })
  }

  const handleFundraisingToggle = (newEnabled: boolean) => {
    const newValue: FundraisingPublicFundraisersConfigValue = { ...(fundraisingConfig ?? {}), enabled: newEnabled }

    resetFundraisingError('')
    setPendingToggle({
      label: t('partnerConfig.fundraising.publicFundraisers'),
      newEnabled,
      domain: 'fundraising',
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshFundraising
        updateFundraising({ partnerId, key: 'public_fundraisers', value: newValue })
      }
    })
  }

  const handleDonationMatchingToggle = (newEnabled: boolean) => {
    const newValue: DonationMatchingFamilialConfigValue = { ...(donationMatchingConfig ?? {}), enabled: newEnabled }

    resetDonationMatchingError('')
    setPendingToggle({
      label: t('partnerConfig.donationMatching.familialDonationMatching'),
      newEnabled,
      domain: 'donationMatching',
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshDonationMatching
        updateDonationMatching({ partnerId, key: 'familial_donation_matching', value: newValue })
      }
    })
  }

  const handleDirectToCampaignToggle = (newEnabled: boolean) => {
    // Same donation-matching PUT endpoint and mutation as familial — only the key differs.
    const newValue: DonationMatchingDirectToCampaignConfigValue = {
      ...(directToCampaignConfig ?? {}),
      enabled: newEnabled
    }

    resetDonationMatchingError('')
    setPendingToggle({
      label: t('partnerConfig.donationMatching.directToCampaignMatchRequests'),
      newEnabled,
      domain: 'donationMatching',
      onConfirm: () => {
        refreshOnSuccessRef.current = refreshDirectToCampaign
        updateDonationMatching({ partnerId, key: 'direct_to_campaign_match_requests', value: newValue })
      }
    })
  }

  const isConfirmLoading = givingMutating || donationMutating || fundraisingMutating || donationMatchingMutating

  const domainError: Record<PendingToggle['domain'], string | undefined> = {
    giving: givingMutationError || undefined,
    donation: donationMutationError || undefined,
    fundraising: fundraisingMutationError || undefined,
    donationMatching: donationMatchingMutationError || undefined
  }
  const confirmError = pendingToggle ? domainError[pendingToggle.domain] : undefined

  return {
    pendingToggle,
    setPendingToggle,
    isConfirmLoading,
    confirmError,
    giving: {
      features: {
        data: givingFeatures,
        isLoading: givingFeaturesLoading,
        error: givingFeaturesStatus === HTTP_NOT_FOUND ? undefined : givingFeaturesError || undefined
      },
      emails: {
        data: givingEmails,
        isLoading: givingEmailsLoading,
        error: givingEmailsStatus === HTTP_NOT_FOUND ? undefined : givingEmailsError || undefined
      }
    },
    donation: {
      giftNote: {
        data: donationGiftNote,
        isLoading: donationGiftNoteLoading,
        error: donationGiftNoteStatus === HTTP_NOT_FOUND ? undefined : donationGiftNoteError || undefined
      },
      emails: {
        data: donationEmails,
        isLoading: donationEmailsLoading,
        error: donationEmailsStatus === HTTP_NOT_FOUND ? undefined : donationEmailsError || undefined
      }
    },
    fundraising: {
      publicFundraisers: {
        data: fundraisingConfig,
        isLoading: fundraisingLoading,
        error: fundraisingStatus === HTTP_NOT_FOUND ? undefined : fundraisingError || undefined
      }
    },
    donationMatching: {
      familial: {
        data: donationMatchingConfig,
        isLoading: donationMatchingLoading,
        error: donationMatchingStatus === HTTP_NOT_FOUND ? undefined : donationMatchingError || undefined
      },
      directToCampaign: {
        data: directToCampaignConfig,
        isLoading: directToCampaignLoading,
        error: directToCampaignStatus === HTTP_NOT_FOUND ? undefined : directToCampaignError || undefined
      }
    },
    handleGivingFeaturesToggle,
    handleGivingEmailsToggle,
    handleDonationGiftNoteToggle,
    handleDonationEmailsToggle,
    handleFundraisingToggle,
    handleDonationMatchingToggle,
    handleDirectToCampaignToggle
  }
}
